import React, { useState, useEffect, useMemo } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  DatePicker,
  Space,
  Table,
  Button,
  Spin,
  Tag,
  List,
  Empty,
  Divider,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  ReloadOutlined,
  EyeOutlined,
  RetweetOutlined,
  FileTextOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusCircleOutlined,
  DollarOutlined,
  FireOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useFirestoreRevenue } from '../hooks/useFirestoreRevenue';
import API from '../config/api';
import { 
  RevenueOverview, 
  ServiceAnalyticsItem, 
  ClinicPerformance,
  Billing,
} from './type';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import PaymentPlanModal from './PaymentPlanModal';
import {
  STATUS_CONFIG as REVENUE_STATUS_CONFIG,
  computeBillingNet,
  aggregateBillings,
  RevenuePayload,
} from '../utils/revenue';

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface TxnRecord {
  _id: string;
  billing_id: string;
  patient_name: string;
  patient_id: string;
  consultationType: string;
  consultationFee?: number;
  paymentStatus: string;
  paymentMethod: string;
  totalAmount: number;
  amountPaid: number;
  amountRefunded?: number;
  net?: number;
  billingDate: string;
  notes?: string;
  services?: { service_id?: string; service_name: string; price?: number; quantity?: number; subtotal: number }[];
}

// Display config derived from the canonical STATUS_CONFIG in utils/revenue.ts.
// Sign rules and labels live in utils/revenue.ts — single source of truth.
const STATUS_CONFIG: Record<string, { color: string; label: string; sign: 1 | -1 | 0 }> = {
  paid:       { color: REVENUE_STATUS_CONFIG.paid.color,       label: REVENUE_STATUS_CONFIG.paid.label.ar,       sign: REVENUE_STATUS_CONFIG.paid.sign },
  partial:    { color: REVENUE_STATUS_CONFIG.partial.color,    label: REVENUE_STATUS_CONFIG.partial.label.ar,    sign: REVENUE_STATUS_CONFIG.partial.sign },
  refunded:   { color: REVENUE_STATUS_CONFIG.refunded.color,   label: REVENUE_STATUS_CONFIG.refunded.label.ar,   sign: REVENUE_STATUS_CONFIG.refunded.sign },
  refund_due: { color: REVENUE_STATUS_CONFIG.refund_due.color, label: REVENUE_STATUS_CONFIG.refund_due.label.ar, sign: REVENUE_STATUS_CONFIG.refund_due.sign },
  pending:    { color: REVENUE_STATUS_CONFIG.pending.color,    label: REVENUE_STATUS_CONFIG.pending.label.ar,    sign: REVENUE_STATUS_CONFIG.pending.sign },
  cancelled:  { color: REVENUE_STATUS_CONFIG.cancelled.color,  label: REVENUE_STATUS_CONFIG.cancelled.label.ar,  sign: REVENUE_STATUS_CONFIG.cancelled.sign },
};

const RevenueAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [txnLoading, setTxnLoading] = useState(false);
  const [overview, setOverview] = useState<RevenueOverview | null>(null);
  // Canonical revenue numbers from the new SST endpoint /api/revenue/doctor/:id
  const [revenueSST, setRevenueSST] = useState<RevenuePayload | null>(null);
  const [servicesAnalytics, setServicesAnalytics] = useState<ServiceAnalyticsItem[]>([]);
  const [performance, setPerformance] = useState<ClinicPerformance | null>(null);
  const [transactions, setTransactions] = useState<TxnRecord[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ]);
  const [collapsed, setCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('today');
  const [paymentPlanBilling, setPaymentPlanBilling] = useState<Billing | null>(null);
  
  const navigate = useNavigate();
  const doctorId = localStorage.getItem('doctorId');
  const clinicId = localStorage.getItem('clinicId') || '';

  // ── Real-time refresh trigger ───────────────────────────────────────────────
  // Hook listens to Firestore for pings only; numbers always come from BE.
  // We invoke it for its side effect of refetching when bills change.
  useFirestoreRevenue();

  // Visit count for "today" — still useful as a quick UX number. We compute it
  // from Firestore waiting_list (which IS the source of truth for who walked in),
  // not from billing.
  const [fsVisits, setFsVisits] = useState<number | null>(null);
  useEffect(() => {
    if (activeFilter !== 'today' || !clinicId || !doctorId) { setFsVisits(null); return; }
    const d = new Date();
    const today = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    const unsubscribe = onSnapshot(
      collection(db, 'clinics', clinicId, 'waiting_list', today, 'patients'),
      (snap) => setFsVisits(snap.docs.filter((item) => {
        const patient = item.data();
        return String(patient.visit_owner_doctor_id || patient.doctor_id || '') === doctorId;
      }).length),
      () => setFsVisits(null)
    );
    return unsubscribe;
  }, [activeFilter, clinicId, doctorId]);

  // الصافي — ALWAYS from BE (single source of truth). Falls back to local
  // aggregation of fetched transactions only if the SST endpoint failed.
  const netTotal = useMemo(() => {
    if (revenueSST) return revenueSST.totalRevenue;
    return aggregateBillings(transactions as any).totalRevenue;
  }, [revenueSST, transactions]);

  useEffect(() => {
    fetchAllData();
    fetchTransactions();
  }, [dateRange]);

  const fetchAllData = async () => {
    if (!doctorId) return;

    try {
      setLoading(true);
      
      const params = {
        startDate: dateRange[0]?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1]?.endOf('day').format('YYYY-MM-DD HH:mm:ss')
      };

      const [overviewRes, servicesRes, performanceRes, revenueSSTRes] = await Promise.all([
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.REVENUE_OVERVIEW(doctorId)}`, { params }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.SERVICES_ANALYTICS(doctorId)}`, { params }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.CLINIC_PERFORMANCE(doctorId)}`, { params }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.REVENUE_DOCTOR(doctorId)}`, { params }),
      ]);

      if (overviewRes.data.success) {
        setOverview(overviewRes.data.data);
      }
      if (servicesRes.data.success) {
        setServicesAnalytics(servicesRes.data.data.services || []);
      }
      if (performanceRes.data.success) {
        setPerformance(performanceRes.data.data);
      }
      if (revenueSSTRes.data?.success) {
        setRevenueSST(revenueSSTRes.data.data as RevenuePayload);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    if (!doctorId) return;
    try {
      setTxnLoading(true);
      const params = {
        startDate: dateRange[0]?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1]?.endOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.REVENUE_DOCTOR_BREAKDOWN(doctorId)}`, { params });
      const data: TxnRecord[] = res.data?.data?.items || [];
      // Sort newest first
      data.sort((a, b) => new Date(b.billingDate).getTime() - new Date(a.billingDate).getTime());
      setTransactions(data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTxnLoading(false);
    }
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
      setActiveFilter('custom');
    }
  };

  const handlePresetPeriod = (period: 'today' | 'week' | 'month' | 'year') => {
    setActiveFilter(period);
    switch (period) {
      case 'today':
        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
        break;
      case 'week':
        setDateRange([dayjs().startOf('week'), dayjs().endOf('week')]);
        break;
      case 'month':
        setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
        break;
      case 'year':
        setDateRange([dayjs().startOf('year'), dayjs().endOf('year')]);
        break;
    }
  };

  const canonicalConsultationTypes = useMemo(() => {
    const byType = new Map<string, { type: string; count: number; revenue: number; patients: Set<string> }>();

    for (const txn of transactions) {
      const servicesSubtotal = (txn.services || []).reduce((sum, s) => sum + (Number(s.subtotal) || 0), 0);
      const explicitConsultationFee = Number(txn.consultationFee) || 0;
      const looksLikeConsultationOnly = servicesSubtotal === 0 && !String(txn.consultationType || '').includes('خدمات');
      const consultationFee = explicitConsultationFee || (looksLikeConsultationOnly ? Number(txn.totalAmount) || 0 : 0);
      if (consultationFee <= 0) continue;

      const billNet = txn.net !== undefined ? Number(txn.net) || 0 : computeBillingNet(txn as any);
      const totalAmount = Number(txn.totalAmount) || 0;
      const consultationNet = totalAmount > 0 && servicesSubtotal > 0
        ? billNet * (consultationFee / totalAmount)
        : billNet;
      if (Math.abs(consultationNet) < 0.001) continue;

      const type = txn.consultationType || 'كشف';
      const item = byType.get(type) || { type, count: 0, revenue: 0, patients: new Set<string>() };
      item.revenue += consultationNet;
      if (!item.patients.has(txn.patient_id)) {
        item.patients.add(txn.patient_id);
        item.count += 1;
      }
      byType.set(type, item);
    }

    return Array.from(byType.values())
      .map(({ patients, ...item }) => ({ ...item, revenue: Math.round(item.revenue * 100) / 100 }))
      .sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
  }, [transactions]);

  const canonicalServicesAnalytics = useMemo<ServiceAnalyticsItem[]>(() => {
    const byService = new Map<string, ServiceAnalyticsItem & { patientIds: Set<string> }>();

    for (const txn of transactions) {
      const services = txn.services || [];
      if (!services.length) continue;

      const billNet = txn.net !== undefined ? Number(txn.net) || 0 : computeBillingNet(txn as any);
      if (Math.abs(billNet) < 0.001) continue;

      const servicesSubtotal = services.reduce((sum, s) => sum + (Number(s.subtotal) || 0), 0);
      const totalAmount = Number(txn.totalAmount) || 0;
      const servicePool = totalAmount > 0 && servicesSubtotal > 0
        ? billNet * (servicesSubtotal / totalAmount)
        : billNet;

      for (const service of services) {
        const subtotal = Number(service.subtotal) || 0;
        const share = servicesSubtotal > 0 ? subtotal / servicesSubtotal : 1 / services.length;
        const revenue = servicePool * share;
        if (Math.abs(revenue) < 0.001) continue;

        const name = service.service_name || 'خدمة';
        const key = service.service_id || name;
        const item = byService.get(key) || {
          service_id: key,
          service_name: name,
          totalPatients: 0,
          totalQuantity: 0,
          totalRevenue: 0,
          averageRevenuePerPatient: 0,
          patientIds: new Set<string>(),
        };

        item.totalRevenue += revenue;
        item.totalQuantity += Number(service.quantity) || 1;
        if (!item.patientIds.has(txn.patient_id)) {
          item.patientIds.add(txn.patient_id);
          item.totalPatients += 1;
        }
        byService.set(key, item);
      }
    }

    return Array.from(byService.values())
      .map(({ patientIds, ...item }) => ({
        ...item,
        totalRevenue: Math.round(item.totalRevenue * 100) / 100,
        averageRevenuePerPatient: item.totalPatients > 0
          ? Math.round((item.totalRevenue / item.totalPatients) * 100) / 100
          : 0,
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [transactions]);

  const canonicalServicesTotal = useMemo(
    () => canonicalServicesAnalytics.reduce((sum, item) => sum + (Number(item.totalRevenue) || 0), 0),
    [canonicalServicesAnalytics]
  );

  /** All configured consultation / visit types, highest revenue first. */
  const consultationTypesSorted = useMemo(() => {
    if (canonicalConsultationTypes.length > 0) return canonicalConsultationTypes;
    const list = performance?.consultationTypes ? [...performance.consultationTypes] : [];
    list.sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    return list;
  }, [canonicalConsultationTypes, performance?.consultationTypes]);

  const TYPE_CARD_STYLES: Array<{ border: string; iconColor: string; iconBg: string }> = [
    { border: '#52c41a', iconColor: '#52c41a', iconBg: '#f6ffed' },
    { border: '#722ed1', iconColor: '#722ed1', iconBg: '#f9f0ff' },
    { border: '#1890ff', iconColor: '#1890ff', iconBg: '#e6f7ff' },
    { border: '#13c2c2', iconColor: '#13c2c2', iconBg: '#e6fffb' },
    { border: '#fa8c16', iconColor: '#fa8c16', iconBg: '#fff7e6' },
    { border: '#eb2f96', iconColor: '#eb2f96', iconBg: '#fff0f6' },
  ];

  const getTypeCardStyle = (type: string, index: number) => {
    if (type === 'كشف') return TYPE_CARD_STYLES[0];
    if (type === 'اعاده كشف' || type === 'إعادة كشف') return TYPE_CARD_STYLES[1];
    return TYPE_CARD_STYLES[index % TYPE_CARD_STYLES.length];
  };

  const getTypeIcon = (type: string, accent: string) => {
    const iconStyle = { fontSize: 28, color: accent };
    if (type === 'كشف') return <EyeOutlined style={iconStyle} />;
    if (type === 'اعاده كشف' || type === 'إعادة كشف') {
      return <RetweetOutlined style={iconStyle} />;
    }
    return <MedicineBoxOutlined style={iconStyle} />;
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ar-EG', { 
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(value);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      
      <Layout>
        <DashboardHeader 
          onSettingsClick={() => navigate('/settings')}
          onMenuClick={() => {}}
          isMobile={false}
        />
        
        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5' }}>
          {/* Header */}
          <Card style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <Button 
                    icon={<ArrowLeftOutlined />} 
                    onClick={() => navigate('/dashboard')}
                  >
                    Back
                  </Button>
                  <Title level={4} style={{ margin: 0 }}>
                    📊 Revenue Report
                  </Title>
                </Space>
              </Col>
              <Col>
                <Space wrap>
                  <Button 
                    type={activeFilter === 'today' ? 'primary' : 'default'}
                    onClick={() => handlePresetPeriod('today')}
                  >
                    Today
                  </Button>
                  <Button 
                    type={activeFilter === 'week' ? 'primary' : 'default'}
                    onClick={() => handlePresetPeriod('week')}
                  >
                    This Week
                  </Button>
                  <Button 
                    type={activeFilter === 'month' ? 'primary' : 'default'}
                    onClick={() => handlePresetPeriod('month')}
                  >
                    This Month
                  </Button>
                  <Button 
                    type={activeFilter === 'year' ? 'primary' : 'default'}
                    onClick={() => handlePresetPeriod('year')}
                  >
                    This Year
                  </Button>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                  />
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => { fetchAllData(); fetchTransactions(); }}
                    loading={loading || txnLoading}
                  >
                    Refresh
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <Spin spinning={loading}>
            {/* SECTION 1: Total Revenue — single source of truth (BE) */}
            <Card
              style={{
                marginBottom: 16,
                background: activeFilter === 'today'
                  ? 'linear-gradient(135deg, #fa541c 0%, #d4380d 100%)'
                  : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none'
              }}
            >
              <Row align="middle" justify="center">
                <Col>
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 18 }}>
                      {activeFilter === 'today'
                        ? <><FireOutlined /> الصافي اليوم</>
                        : '💰 الصافي'}
                    </Text>
                    <Title level={1} style={{ color: '#fff', margin: '8px 0', fontSize: 48 }}>
                      {loading ? '...' : formatCurrency(netTotal)} EGP
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
                      {dateRange[0]?.format('DD/MM/YYYY')} - {dateRange[1]?.format('DD/MM/YYYY')}
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* SECTION 2: Revenue by every visit / consultation type */}
            <Card title="💠 Revenue by visit type" style={{ marginBottom: 16 }}>
              {consultationTypesSorted.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {consultationTypesSorted.map((ct, idx) => {
                    const style = getTypeCardStyle(ct.type, idx);
                    return (
                      <Col xs={24} sm={12} lg={8} xl={6} key={`${ct.type}-${idx}`}>
                        <Card
                          style={{ height: '100%', borderLeft: `4px solid ${style.border}` }}
                          bodyStyle={{ padding: 20 }}
                        >
                          <Row align="middle" gutter={12} wrap={false}>
                            <Col flex="none">
                              <div
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: '50%',
                                  background: style.iconBg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {getTypeIcon(ct.type, style.iconColor)}
                              </div>
                            </Col>
                            <Col flex="auto" style={{ minWidth: 0 }}>
                              <Text type="secondary" ellipsis title={ct.type}>
                                {ct.type || '—'}
                              </Text>
                              <Title level={3} style={{ margin: '4px 0', color: style.iconColor }}>
                                {ct.count}
                              </Title>
                              <Text strong style={{ fontSize: 16 }}>
                                {formatCurrency(ct.revenue || 0)} EGP
                              </Text>
                            </Col>
                          </Row>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <Empty description="No visit-type revenue for this period" />
              )}
            </Card>

            {/* SECTION 3: Revenue Breakdown */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} lg={12}>
                <Card>
                  <Statistic
                    title="Services Revenue"
                    value={canonicalServicesAnalytics.length > 0 ? canonicalServicesTotal : (overview?.totalServicesRevenue || 0)}
                    suffix="EGP"
                    prefix={<MedicineBoxOutlined style={{ color: '#13c2c2' }} />}
                    valueStyle={{ color: '#13c2c2' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Card>
                  <Statistic
                    title="Total Bills"
                    value={revenueSST?.billingCount ?? overview?.totalBillings ?? 0}
                    prefix={<CalendarOutlined style={{ color: '#52c41a' }} />}
                  />
                </Card>
              </Col>
            </Row>

            {/* SECTION 4: Quick Stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}>
                <Card style={{ textAlign: 'center' }}>
                  <Text type="secondary">Total Visits</Text>
                  <Title level={3} style={{ margin: '8px 0' }}>
                    {activeFilter === 'today' && fsVisits !== null
                      ? fsVisits
                      : performance?.overview.clinicalVisitCount ?? performance?.overview.totalVisits ?? 0}
                  </Title>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card style={{ textAlign: 'center' }}>
                  <Text type="secondary">Unique Patients</Text>
                  <Title level={3} style={{ margin: '8px 0' }}>
                    {performance?.overview.uniquePatientCount || 0}
                  </Title>
                </Card>
              </Col>
            </Row>

            {/* SECTION 5: All Consultation Types */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}>
                <Card title="📋 All Consultation Types">
                  {consultationTypesSorted.length > 0 ? (
                    <List
                      dataSource={consultationTypesSorted}
                      renderItem={(item: any) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <Space>
                                <Tag color={item.type === 'كشف' ? 'green' : item.type === 'اعاده كشف' ? 'purple' : 'blue'}>
                                  {item.type}
                                </Tag>
                                <Text strong>{item.count} patients</Text>
                              </Space>
                            }
                          />
                          <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                            {formatCurrency(item.revenue)} EGP
                          </Text>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty description="No consultation data" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title="💳 Payment Methods">
                  {performance?.paymentMethods && performance.paymentMethods.length > 0 ? (
                    <List
                      dataSource={performance.paymentMethods}
                      renderItem={(item: any) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <Space>
                                <Tag color={
                                  item.method === 'cash' ? 'green' : 
                                  item.method === 'card' ? 'blue' : 
                                  item.method === 'insurance' ? 'purple' : 'default'
                                }>
                                  {item.method === 'cash' ? '💵 Cash' : 
                                   item.method === 'card' ? '💳 Card' : 
                                   item.method === 'insurance' ? '🏥 Insurance' : item.method}
                                </Tag>
                                <Text>{item.count} transactions</Text>
                              </Space>
                            }
                          />
                          <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                            {formatCurrency(item.amount)} EGP
                          </Text>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty description="No payment data" />
                  )}
                </Card>
              </Col>
            </Row>

            {/* SECTION 6: Services Revenue */}
            <Card title="🏥 Additional Services Revenue" style={{ marginBottom: 16 }}>
              {(canonicalServicesAnalytics.length > 0 ? canonicalServicesAnalytics : servicesAnalytics).length > 0 ? (
                <Table
                  dataSource={canonicalServicesAnalytics.length > 0 ? canonicalServicesAnalytics : servicesAnalytics}
                  rowKey="service_id"
                  pagination={false}
                  columns={[
                    {
                      title: 'Service Name',
                      dataIndex: 'service_name',
                      key: 'service_name',
                      render: (text: string) => <Text strong>{text}</Text>
                    },
                    {
                      title: 'Patients',
                      dataIndex: 'totalPatients',
                      key: 'totalPatients',
                      align: 'center' as const,
                      render: (value: number) => (
                        <Tag color="blue">{value}</Tag>
                      )
                    },
                    {
                      title: 'Times Used',
                      dataIndex: 'totalQuantity',
                      key: 'totalQuantity',
                      align: 'center' as const
                    },
                    {
                      title: 'Total Revenue',
                      dataIndex: 'totalRevenue',
                      key: 'totalRevenue',
                      align: 'right' as const,
                      render: (value: number) => (
                        <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                          {formatCurrency(value)} EGP
                        </Text>
                      ),
                      sorter: (a: ServiceAnalyticsItem, b: ServiceAnalyticsItem) => 
                        a.totalRevenue - b.totalRevenue,
                      defaultSortOrder: 'descend' as const
                    },
                    {
                      title: 'Avg per Patient',
                      dataIndex: 'averageRevenuePerPatient',
                      key: 'averageRevenuePerPatient',
                      align: 'right' as const,
                      render: (value: number) => `${formatCurrency(value)} EGP`
                    }
                  ]}
                  summary={(pageData) => {
                    const totalRev = pageData.reduce((sum, item) => sum + item.totalRevenue, 0);
                    const totalPatients = pageData.reduce((sum, item) => sum + item.totalPatients, 0);
                    return (
                      <Table.Summary.Row style={{ background: '#fafafa' }}>
                        <Table.Summary.Cell index={0}>
                          <Text strong>Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="center">
                          <Tag color="green">{totalPatients}</Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="center">-</Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <Text strong style={{ color: '#52c41a', fontSize: 18 }}>
                            {formatCurrency(totalRev)} EGP
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">-</Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              ) : (
                <Empty description="No services data for selected period" />
              )}
            </Card>

            {/* SECTION 7: Transactions Statement — hidden, kept for future use */}
            {false && (() => {
              // Net impact per transaction — uses canonical computeBillingNet from utils/revenue.ts
              const txnWithNet = transactions.map((t) => ({ ...t, _net: computeBillingNet(t as any) }));

              // Totals come from the SST endpoint when available; the per-row values are
              // computed identically here for the table, so totals match by construction.
              const totalIn  = revenueSST
                ? revenueSST!.amountCollected
                : txnWithNet.filter(t => t._net > 0).reduce((s, t) => s + t._net, 0);
              const totalOut = revenueSST
                ? -revenueSST!.refundedAmount
                : txnWithNet.filter(t => t._net < 0).reduce((s, t) => s + t._net, 0);
              const netTotal = revenueSST ? revenueSST!.totalRevenue : (totalIn + totalOut);

              const txnColumns = [
                {
                  title: 'التاريخ والوقت',
                  dataIndex: 'billingDate',
                  key: 'billingDate',
                  width: 150,
                  render: (v: string) => {
                    const d = new Date(v);
                    return (
                      <div style={{ fontSize: 12 }}>
                        <div>{d.toLocaleDateString('ar-EG')}</div>
                        <div style={{ color: '#8c8c8c' }}>{d.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    );
                  },
                  sorter: (a: any, b: any) => new Date(a.billingDate).getTime() - new Date(b.billingDate).getTime(),
                  defaultSortOrder: 'ascend' as const,
                },
                {
                  title: 'المريض',
                  dataIndex: 'patient_name',
                  key: 'patient_name',
                  render: (v: string) => <Text strong>{v || '—'}</Text>,
                },
                {
                  title: 'النوع / الوصف',
                  dataIndex: 'consultationType',
                  key: 'consultationType',
                  render: (v: string, rec: any) => (
                    <Tooltip title={rec.notes || ''}>
                      <span>{v || '—'}</span>
                      {rec.services && rec.services.length > 0 && (
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                          {rec.services.map((s: any) => s.service_name).join(', ')}
                        </div>
                      )}
                    </Tooltip>
                  ),
                },
                {
                  title: 'الحالة',
                  dataIndex: 'paymentStatus',
                  key: 'paymentStatus',
                  render: (v: string) => {
                    const cfg = STATUS_CONFIG[v] ?? STATUS_CONFIG['pending'];
                    return <Tag color={cfg.color}>{cfg.label}</Tag>;
                  },
                  filters: Object.entries(STATUS_CONFIG).map(([k, v]) => ({ text: v.label, value: k })),
                  onFilter: (value: any, record: any) => record.paymentStatus === value,
                },
                {
                  title: 'طريقة الدفع',
                  dataIndex: 'paymentMethod',
                  key: 'paymentMethod',
                  render: (v: string) =>
                    v === 'cash' ? '💵 نقداً' : v === 'card' ? '💳 كارت' : v === 'insurance' ? '🏥 تأمين' : v || '—',
                },
                {
                  title: 'المبلغ (ج.م)',
                  key: 'net',
                  align: 'right' as const,
                  render: (_: any, rec: any) => {
                    const cfg = STATUS_CONFIG[rec.paymentStatus] ?? STATUS_CONFIG['pending'];
                    const amount = Math.abs(rec.amountPaid || rec.totalAmount || 0);
                    const remaining = Math.max(0, (rec.totalAmount || 0) - (rec.amountPaid || 0));
                    if (cfg.sign === 1) return (
                      <Space direction="vertical" size={0}>
                        <Space>
                          <ArrowUpOutlined style={{ color: '#52c41a' }} />
                          <Text strong style={{ color: '#52c41a' }}>+{formatCurrency(amount)}</Text>
                        </Space>
                        {rec.paymentStatus === 'partial' && remaining > 0 && (
                          <Text style={{ fontSize: 11, color: '#fa8c16' }}>متبقي: {formatCurrency(remaining)} ج</Text>
                        )}
                      </Space>
                    );
                    if (cfg.sign === -1) return (
                      <Space>
                        <ArrowDownOutlined style={{ color: '#f5222d' }} />
                        <Text strong style={{ color: cfg.color }}>−{formatCurrency(Math.abs(rec.totalAmount || 0))}</Text>
                      </Space>
                    );
                    return (
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{formatCurrency(amount)}</Text>
                        {rec.paymentStatus === 'pending' && rec.totalAmount > 0 && (
                          <Text style={{ fontSize: 11, color: '#fa8c16' }}>الكل معلق: {formatCurrency(rec.totalAmount)} ج</Text>
                        )}
                      </Space>
                    );
                  },
                  sorter: (a: any, b: any) => Math.abs(a.amountPaid || a.totalAmount) - Math.abs(b.amountPaid || b.totalAmount),
                },
                {
                  title: '',
                  key: 'actions',
                  width: 50,
                  render: (_: any, rec: any) => {
                    if (rec.paymentStatus !== 'pending' && rec.paymentStatus !== 'partial') return null;
                    return (
                      <Tooltip title="إدارة الدفعات">
                        <Button
                          size="small"
                          type="primary"
                          icon={<DollarOutlined />}
                          style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                          onClick={() => setPaymentPlanBilling(rec as unknown as Billing)}
                        />
                      </Tooltip>
                    );
                  },
                },
              ];

              return (
                <Card
                  title={
                    <Space>
                      <FileTextOutlined style={{ color: '#1890ff' }} />
                      <span>📋 كشف الحساب — Transactions Statement</span>
                      <Tag color="blue">{transactions.length} عملية</Tag>
                    </Space>
                  }
                  extra={
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={txnLoading}
                      onClick={fetchTransactions}
                    >
                      تحديث
                    </Button>
                  }
                >
                  {/* Summary bar */}
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={8}>
                      <div style={{ textAlign: 'center', padding: '12px 0', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                        <ArrowUpOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>إجمالي الدخل</div>
                        <div style={{ color: '#52c41a', fontWeight: 700, fontSize: 20 }}>+{formatCurrency(totalIn)} ج</div>
                      </div>
                    </Col>
                    <Col xs={24} sm={8}>
                      <div style={{ textAlign: 'center', padding: '12px 0', background: '#fff2f0', borderRadius: 8, border: '1px solid #ffccc7' }}>
                        <ArrowDownOutlined style={{ color: '#f5222d', fontSize: 18 }} />
                        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>إجمالي المسترد</div>
                        <div style={{ color: '#f5222d', fontWeight: 700, fontSize: 20 }}>{formatCurrency(totalOut)} ج</div>
                      </div>
                    </Col>
                    <Col xs={24} sm={8}>
                      <div style={{
                        textAlign: 'center', padding: '12px 0', borderRadius: 8,
                        background: netTotal >= 0 ? '#e6f7ff' : '#fff2f0',
                        border: `1px solid ${netTotal >= 0 ? '#91d5ff' : '#ffccc7'}`,
                      }}>
                        <MinusCircleOutlined style={{ color: netTotal >= 0 ? '#1890ff' : '#f5222d', fontSize: 18 }} />
                        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>الصافي</div>
                        <div style={{ color: netTotal >= 0 ? '#1890ff' : '#f5222d', fontWeight: 700, fontSize: 20 }}>
                          {netTotal >= 0 ? '+' : ''}{formatCurrency(netTotal)} ج
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Divider style={{ margin: '8px 0 16px' }} />

                  <Spin spinning={txnLoading}>
                    {transactions.length > 0 ? (
                      <Table
                        dataSource={txnWithNet}
                        rowKey={(r) => r.billing_id || r._id}
                        pagination={{ pageSize: 40, showSizeChanger: true, pageSizeOptions: ['20', '40', '100'], showTotal: (t) => `${t} عملية` }}
                        size="small"
                        columns={txnColumns}
                        onRow={(rec: any) => ({
                          style: {
                            background:
                              rec.paymentStatus === 'refunded'   ? '#fff2f0' :
                              rec.paymentStatus === 'refund_due' ? '#fff7e6' :
                              rec.paymentStatus === 'paid'       ? '#f6ffed' : 'transparent',
                          },
                        })}
                        summary={() => (
                          <Table.Summary fixed>
                            <Table.Summary.Row>
                              <Table.Summary.Cell index={0} colSpan={5}>
                                <Text strong>الصافي الكلي للفترة</Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={5} align="right">
                                <Text strong style={{ fontSize: 16, color: netTotal >= 0 ? '#52c41a' : '#f5222d' }}>
                                  {netTotal >= 0 ? '+' : ''}{formatCurrency(netTotal)} ج
                                </Text>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          </Table.Summary>
                        )}
                      />
                    ) : (
                      <Empty description="لا توجد معاملات في هذه الفترة" />
                    )}
                  </Spin>
                </Card>
              );
            })()} 

          </Spin>
        </Content>
      </Layout>

      {paymentPlanBilling && (
        <PaymentPlanModal
          visible={!!paymentPlanBilling}
          billing={paymentPlanBilling}
          onCancel={() => setPaymentPlanBilling(null)}
          onUpdated={(updated) => {
            setTransactions(prev =>
              prev.map(t => t.billing_id === updated.billing_id ? { ...t, ...updated } : t)
            );
            if (updated.paymentStatus === 'paid') setPaymentPlanBilling(null);
            else setPaymentPlanBilling(updated);
          }}
        />
      )}
    </Layout>
  );
};

export default RevenueAnalytics;
